import React, { useState } from 'react';
import cn from 'classnames';
import { SlideOver } from './primitives';
import { tierClass } from './helpers';
import { getConciergeShopperDetail } from '../../../api/noir';

// ShopMy "Circles" mark — two overlapping rings. Inline so it inherits color via currentColor.
const CirclesIcon = () => (
  <svg viewBox='0 0 24 24' width='13' height='13' fill='none' aria-hidden='true'>
    <circle cx='9' cy='12' r='6' stroke='currentColor' strokeWidth='1.7' />
    <circle cx='15' cy='12' r='6' stroke='currentColor' strokeWidth='1.7' />
  </svg>
);

const money = v => (v == null || isNaN(v) ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// Imported-receipt source pills (anything that isn't ShopMy-link spend).
const SOURCE_LABELS = { gmail: 'Gmail', plaid: 'Plaid', extension: 'Extension' };

const fmtDate = d => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt) ? '' : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const ShopperListSlideOver = ({ cohort, onClose }) => {
  const [expanded, setExpanded] = useState(null);
  // Recency window the preview was built with — the custom exclusion when set, 14-day default otherwise.
  const windowDays = cohort.recentWindowDays || 14;
  // Lazy-loaded per-shopper detail keyed by user id: { loading, error, noirCurators, purchases }.
  const [detailById, setDetailById] = useState({});
  // Per-purchase product dropdowns, keyed `${shopperId}:${purchaseIndex}`.
  const [openPurchases, setOpenPurchases] = useState({});
  const togglePurchase = key => setOpenPurchases(p => ({ ...p, [key]: !p[key] }));

  const toggle = id => {
    const next = expanded === id ? null : id;
    setExpanded(next);
    if (next && !detailById[id]) {
      setDetailById(p => ({ ...p, [id]: { loading: true } }));
      getConciergeShopperDetail(id)
        .then(d => setDetailById(p => ({ ...p, [id]: { loading: false, ...d } })))
        .catch(() => setDetailById(p => ({ ...p, [id]: { loading: false, error: true } })));
    }
  };

  return (
    <SlideOver
      title='Cohort Preview'
      subtitle={`${cohort.withPhone} recipients · ${cohort.recentlyMessagedCount} messaged in last ${windowDays} days`}
      onClose={onClose}
      className='list'
      resizable
      defaultWidth={780}
    >
      <table className='nb-shopper-list'>
        <thead>
          <tr>
            {['', 'Name', 'Tier', 'Score', 'Last SMS', 'Purchase Status'].map((h, i) => (
              <th key={h || `c${i}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohort.matched.map(s => {
            const detail = detailById[s.id];
            const isOpen = expanded === s.id;
            return (
              <React.Fragment key={s.id}>
                <tr className={cn('shopper-row', { recent: s.lastSmsDate <= windowDays, expanded: isOpen })} onClick={() => toggle(s.id)}>
                  <td className='nb-expand-cell'>
                    <span className={cn('nb-row-caret', { open: isOpen })}>▾</span>
                  </td>
                  <td>
                    <div className='nb-name-cell'>
                      {s.lastSmsDate <= windowDays && <span className='nb-dot' title={`Messaged in last ${windowDays} days`} />}
                      <span className='nb-shopper-name'>{s.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className={cn('nb-tier-badge', tierClass(s.tier))}>{s.tier}</span>
                  </td>
                  <td className='score-cell'>{s.score}</td>
                  <td className='muted'>{s.lastSms}</td>
                  <td>{s.purchaseStatus}</td>
                </tr>
                {isOpen && (
                  <tr className='nb-shopper-detail-row'>
                    <td colSpan={6}>
                      {!detail || detail.loading ? (
                        <p className='nb-detail-loading'>Loading…</p>
                      ) : detail.error ? (
                        <p className='nb-detail-loading'>Failed to load details.</p>
                      ) : (
                        <div className='nb-shopper-detail'>
                          <div className='nb-detail-block'>
                            <p className='nb-detail-label'>Noir curator affinity</p>
                            {detail.noirCurators && detail.noirCurators.length > 0 ? (
                              <ul className='nb-curator-list'>
                                {detail.noirCurators.map(c => (
                                  <li key={c.id}>
                                    <span className='nb-curator-name'>{c.name || c.username || `#${c.id}`}</span>
                                    {c.inCircle && (
                                      <span
                                        className='nb-circle-icon'
                                        title={c.circles ? `In their circle: ${c.circles}` : 'In their circle'}
                                      >
                                        <CirclesIcon />
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className='nb-detail-empty'>None</p>
                            )}
                          </div>
                          <div className='nb-detail-block nb-purchases-block'>
                            <p className='nb-detail-label'>Last {Math.min(detail.purchases?.length || 0, 10)} purchases</p>
                            {detail.purchases && detail.purchases.length > 0 ? (
                              <ul className='nb-purchase-list'>
                                {detail.purchases.map((p, i) => {
                                  const items = p.items || [];
                                  const purchaseKey = `${s.id}:${i}`;
                                  const isPurchaseOpen = !!openPurchases[purchaseKey];
                                  return (
                                    <li key={i} className='nb-purchase-item'>
                                      <div
                                        className={cn('nb-purchase-head', { expandable: items.length > 0 })}
                                        onClick={items.length > 0 ? () => togglePurchase(purchaseKey) : undefined}
                                      >
                                        {items.length > 0 && <span className={cn('nb-purchase-caret', { open: isPurchaseOpen })}>▾</span>}
                                        <span className='nb-purchase-retailer'>{p.retailer || '—'}</span>
                                        {items.length > 0 && (
                                          <span className='nb-purchase-count'>
                                            {items.length} item{items.length === 1 ? '' : 's'}
                                          </span>
                                        )}
                                        {p.viaNoir ? <span className='nb-noir-tag'>Noir</span> : null}
                                        {p.source && p.source !== 'shopmy' ? (
                                          <span className='nb-source-tag'>{SOURCE_LABELS[p.source] || p.source}</span>
                                        ) : null}
                                        <span className='nb-purchase-price'>{money(p.price)}</span>
                                      </div>
                                      {isPurchaseOpen && items.length > 0 && (
                                        <ul className='nb-purchase-items'>
                                          {items.map((item, j) => (
                                            <li key={j} className='nb-purchase-product-row'>
                                              <span className='nb-purchase-product-detail'>
                                                {item.brand && <span className='nb-purchase-product-brand'>{item.brand}</span>}
                                                {item.brand && <span className='nb-purchase-sep'>·</span>}
                                                <span className='nb-purchase-product'>{item.title}</span>
                                              </span>
                                              {item.price != null && <span className='nb-purchase-product-price'>{money(item.price)}</span>}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                      {p.date && <div className='nb-purchase-date'>{fmtDate(p.date)}</div>}
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <p className='nb-detail-empty'>No purchases</p>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <div className='nb-slideover-legend'>
        <span className='nb-legend-item'>
          <span className='nb-legend-dot' /> Messaged in last {windowDays} days (amber row)
        </span>
      </div>
    </SlideOver>
  );
};

export default ShopperListSlideOver;
